import individualRules from "@content/rules/individual.json";
import type { IndicatorAnswer, ResolvedRouting } from "./types";

type ModeRules = {
  entryPaths: {
    infoOnly: { modules: string[]; resourceCategories: string[] };
    unsafeToAnswer: { modules: string[]; resourceCategories: string[] };
    contactNow: { modules: string[]; resourceCategories: string[] };
  };
  questionFlow: {
    order: string[];
    onSkipAll: { modules: string[]; resourceCategories: string[] };
  };
  defaultAfterQuestions: {
    modules: string[];
    resourceCategories: string[];
  };
  mappings: Array<{
    id: string;
    whenAnyYes: string[];
    modules: string[];
    resourceCategories: string[];
  }>;
};

const rules = individualRules as ModeRules;

function unique(items: string[]): string[] {
  return [...new Set(items)];
}

function unionRouting(
  parts: Array<{ modules: string[]; resourceCategories: string[] }>,
): Omit<ResolvedRouting, "hasAnyYes"> {
  return {
    modules: unique(parts.flatMap((p) => p.modules)),
    resourceCategories: unique(parts.flatMap((p) => p.resourceCategories)),
  };
}

/** Question ids in approved Item 5 order. */
export function getQuestionOrder(): string[] {
  return [...rules.questionFlow.order];
}

/**
 * Deterministic, invisible routing. Never returns scores or verdicts.
 * Only `yes` answers activate mappings.
 */
export function resolveFromAnswers(
  answers: Record<string, IndicatorAnswer>,
): ResolvedRouting {
  const yesKeys = Object.entries(answers)
    .filter(([, value]) => value === "yes")
    .map(([key]) => key);

  if (yesKeys.length === 0) {
    const base = rules.defaultAfterQuestions;
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: false,
    };
  }

  const matched = rules.mappings.filter((map) =>
    map.whenAnyYes.some((id) => yesKeys.includes(id)),
  );

  if (matched.length === 0) {
    const base = rules.defaultAfterQuestions;
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: true,
    };
  }

  const unioned = unionRouting(matched);
  return { ...unioned, hasAnyYes: true };
}

export function resolveInfoOnly(): ResolvedRouting {
  const path = rules.entryPaths.infoOnly;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveUnsafeToAnswer(): ResolvedRouting {
  const path = rules.entryPaths.unsafeToAnswer;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveContactNow(): ResolvedRouting {
  const path = rules.entryPaths.contactNow;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveSkipAll(): ResolvedRouting {
  const path = rules.questionFlow.onSkipAll;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}
